import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["assigned", "picked_up", "in_transit", "delivered"];

export default function DriverDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) return nav("/login", { state: { next: "/driver" } });
    if (user && user.role !== "driver") return nav("/");
  }, [user, nav]);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/bookings");
      setBookings(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "driver") refresh(); }, [user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/driver/bookings/${id}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user.role !== "driver") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20" data-testid="driver-dashboard">
      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Driver</span>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Hello {user.name}</h1>
      <p className="text-muted-foreground mt-3">Your assigned trips. Update status when you collect, transit, or deliver.</p>

      {loading ? (
        <p className="mt-10 text-muted-foreground">Loading…</p>
      ) : bookings.length === 0 ? (
        <Card className="p-10 mt-10 text-center border-border">
          <p className="text-muted-foreground">No trips assigned yet.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {bookings.map((b) => (
            <Card key={b.id} className="p-5 border-border" data-testid={`driver-booking-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-muted-foreground">{b.pickup_date} · {b.distance_miles}mi</p>
                  <h3 className="font-display text-base mt-1">{b.pickup_location} → {b.dropoff_location}</h3>
                </div>
                <Badge>{b.status.replace("_", " ")}</Badge>
              </div>
              <div className="text-sm mt-3">
                <p>{b.customer_name}</p>
                <p className="text-muted-foreground">{b.customer_email}</p>
              </div>
              <div className="text-sm mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">Pet</p>
                <p>{b.pet_name} · {b.pet_type} {b.pet_breed && `(${b.pet_breed})`}</p>
                {b.special_notes && <p className="text-xs text-muted-foreground mt-1">{b.special_notes}</p>}
              </div>
              <div className="mt-4">
                <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                  <SelectTrigger data-testid={`driver-status-select-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
